import { X } from "lucide-react";
import { Modal, type ModalProps } from "components/ui/v2/modal";
import {
  cloneElement,
  FC,
  isValidElement,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { IOpenModal, ModalContext } from "./modal.context";

type StoredModalProps = Omit<
  IOpenModal,
  "modalComponent" | "onOpenModal" | "onCloseModal"
>;

const normalizeSize = (
  size?: StoredModalProps["size"],
  full?: boolean
): ModalProps["size"] => {
  if (full) return "full";
  return size || "md";
};

const ModalProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [modal, setModal] = useState<JSX.Element | null>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<StoredModalProps>();
  const [modalFooter, setModalFooter] = useState<ReactNode>(undefined);

  const [onOpen, setOnOpen] = useState<() => void>(() => {});
  const [onClose, setOnClose] = useState<() => void>(() => {});

  useEffect(() => {
    if (isOpen) {
      if (onOpen) onOpen();
    } else if (onClose) {
      onClose();
    }
  }, [isOpen, onClose, onOpen]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setOnOpen(() => {});
    setModalFooter(undefined);

    setTimeout(() => {
      setModal(null);
      setOnClose(() => {});
    }, 500);
  }, []);

  const injectFooterPublisher = useCallback(
    (modalComponent: JSX.Element) => {
      if (!isValidElement(modalComponent)) return modalComponent;

      const childOnFooterChange = (
        modalComponent.props as { onFooterChange?: (footer: ReactNode) => void }
      ).onFooterChange;
      const childOnClose = (
        modalComponent.props as { onClose?: () => void }
      ).onClose;

      return cloneElement(modalComponent, {
        onFooterChange: (footer: ReactNode) => {
          setModalFooter(footer);
          childOnFooterChange?.(footer);
        },
        onClose: childOnClose ?? closeModal,
      } as Record<string, unknown>);
    },
    [closeModal]
  );

  const setModalComponent = useCallback(
    (newModalComponent: JSX.Element) => {
      setModal(injectFooterPublisher(newModalComponent));
    },
    [injectFooterPublisher]
  );

  const value = useMemo(
    () => ({
      openModal: (params: IOpenModal) => {
        const {
          modalComponent,
          onOpenModal,
          onCloseModal,
          footer: initialFooter,
          ...newModalProps
        } = params;

        setIsOpen(true);
        setModalFooter(initialFooter);
        setModal(injectFooterPublisher(modalComponent));
        setModalProps(newModalProps);

        if (onOpenModal) setOnOpen(() => onOpenModal);
        if (onCloseModal) setOnClose(() => onCloseModal);
      },

      closeModal,
      setModalComponent,
      setModalFooter,
    }),
    [closeModal, injectFooterPublisher]
  );

  const {
    full,
    width,
    height,
    style,
    size,
    className,
    panelClassName,
    bodyClassName,
    backdrop = true,
    keyboard = true,
    hideCloseButton = false,
    unstyledPanel = false,
    ...restModalProps
  } = modalProps || {};

  const hasNativeV2Chrome = Boolean(
    restModalProps.title ||
      restModalProps.eyebrow ||
      restModalProps.header ||
      restModalProps.description ||
      panelClassName ||
      bodyClassName
  );
  const shouldRenderBare = hideCloseButton || unstyledPanel || hasNativeV2Chrome;

  return (
    <ModalContext.Provider {...{ value }}>
      <Modal
        {...restModalProps}
        footer={modalFooter}
        open={isOpen}
        onClose={closeModal}
        size={normalizeSize(size, full)}
        backdrop={backdrop}
        keyboard={keyboard}
        showCloseButton={hasNativeV2Chrome && !hideCloseButton}
        unstyledPanel={unstyledPanel || hideCloseButton}
        className={className}
        panelClassName={panelClassName}
        panelStyle={{ width, height, ...style }}
        bodyClassName={
          bodyClassName ||
          (shouldRenderBare
            ? "bg-transparent p-0 !overflow-visible dark:bg-transparent"
            : undefined)
        }
      >
        {shouldRenderBare ? (
          // V2/self-contained panels bring their own card/header or native Modal chrome.
          // Render them bare to avoid a double modal/frame.
          modal
        ) : (
          <div className="relative p-4 pt-5">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-3 top-3 z-[2] flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-slate-200/70 bg-white/85 text-slate-500 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white hover:text-slate-800 dark:border-zinc-800/80 dark:bg-zinc-900/85 dark:text-zinc-300 dark:hover:bg-zinc-800"
              aria-label="Close modal"
            >
              <X className="h-3 w-3 pointer-events-none text-xs" />
            </button>

            {modal}
          </div>
        )}
      </Modal>

      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => useContext(ModalContext);

export default ModalProvider;
